import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./HoverZoomImage.module.css";

const PREVIEW = 320; // px, longest side of the enlarged photo
const GAP = 12;

/**
 * A photo thumbnail that shows an enlarged copy next to it while the pointer is over it
 * (tap to toggle on touch screens). The preview is portalled to <body> so dialog
 * scroll areas can't clip it.
 *   <HoverZoomImage src={url} alt="Pickup" className={styles.imgDiv} />
 */
export default function HoverZoomImage({ src, alt = "", className = "", contain = false }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const show = () => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Right of the thumbnail when there is room, else left, else centred over it
    let left = box.right + GAP;
    if (left + PREVIEW > vw - GAP) left = box.left - GAP - PREVIEW;
    if (left < GAP) left = Math.max(GAP, (vw - PREVIEW) / 2);
    const top = Math.min(Math.max(GAP, box.top + box.height / 2 - PREVIEW / 2), vh - PREVIEW - GAP);
    setPos({ left, top: Math.max(GAP, top) });
  };
  const hide = () => setPos(null);

  if (!src) return null;

  return (<>
    <div
      ref={ref}
      className={`${styles.Thumb} ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={() => (pos ? hide() : show())}
      role="img"
      aria-label={alt}
    >
      <img src={src} alt={alt} className={contain ? styles.Contain : undefined} />
      <span className={styles.ZoomBadge} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="14" height="14"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Zm.5-7H9v2H7v1h2v2h1v-2h2V9h-2V7Z" /></svg>
      </span>
    </div>
    {pos && createPortal(
      <div className={styles.Preview} style={{ left: pos.left, top: pos.top, width: PREVIEW, height: PREVIEW }} aria-hidden="true">
        <img src={src} alt="" />
      </div>,
      document.body
    )}
  </>);
}
